import DocumentLink from '../documents/DocumentLink'
import { manualMovementHref } from '../manualMovement'
import RecordEditTargets from '../recordEdits/RecordEditTargets'
import type { EditScope } from '../recordEdits/contract'
/* The existing Record table, shared by a real fund and explicit multi-fund
 * contexts. Hierarchy preserves server chain order; alphabetical is optional.
 * A commitment position keeps commitment, drawn and undrawn on three child
 * rows. Unknown amounts remain unknown. Contextual doors use actual object
 * and HII identities; shared quantities are never summed across funds. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetchBlob } from '../../lib/api'
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordContents, RtSegment } from '../RecordTable'
import { formatOfficerDate } from '../../lib/officerFormat'
import { focusRow, useListKeys } from '../keyboard/useListKeys'
import { fundRootId } from './chainTree'
import { isProven, tableDate } from './evidenceRows'
import type { Lens, MatrixRow } from './fundArea'
import PositionLedger from './PositionLedger'
import {
  balanceDemand,
  breakTaskOf,
  breakWords,
  countsFromLedger,
  flatOrder,
  isCommitmentBased,
  positionMovements,
  positionChangedSince,
  positionsFootWords,
  recordCounts,
  statedCommitment,
  type LedgerCounts,
  type LedgerTrade,
  type RecordCounts,
} from './positionLedger'
import {
  RECON_TASK_TYPES,
  balanceOf,
  evOf,
  figure,
  taskDelta,
  type DeriveTask,
} from './recordDerive'
import { throughLine, type MasterLink } from './masters'
import { ErrorState } from '../state'
import { HelpDot } from '../guidance/Guidance'
import { breakItemAsTask, type FundLedger } from '../ledgerWords'

interface Props {
  fundId?: string
  table: RecordContents & { fund_name?: string }
  /** Complete admitted financial snapshot before the evidence display filter. */
  fullTable?: RecordContents
  /** Open workbench tasks scoped to this fund — the RECO chips, the
   *  breaks count, the ledger foot's door. */
  tasks: DeriveTask[]
  /** The proof matrix — EV, the DOCUMENT column, the owed count. */
  matrix: MatrixRow[]
  /** The master funds this feeder holds (16d). A fund-units line whose
   *  issuer is a master carries a "through …" sub-line with a door to the
   *  master's own record; the master's demands are NOT folded into the
   *  feeder's EV or break column. */
  masters?: MasterLink[]
  /** The fund's ledger (B1, Ale 2026-09-04): the count chips read ITS
   *  figures — `countsFromLedger` — instead of the page's own tally, and
   *  its break items join the RECO column so a parked (snoozed) break
   *  still shows its delta. */
  ledger?: (LedgerCounts & Partial<Pick<FundLedger, 'breaks'>>) | null
  /** The active rebuild lens: in the evidenced view EV is a dash (every
   *  line is evidenced by construction). */
  lens?: Lens | null
  contextual?: {
    onPosition: (segment: RtSegment) => void
    breakHref: (segment: RtSegment) => string
    onExport: (segments: RtSegment[]) => void
  }
  allowEdits?: boolean
  editScope?: (segment: RtSegment) => EditScope | null
  onRecordChanged?: () => void
  onOpen: (target: DrawerTarget) => void
  onEvidence: (target: DrawerTarget) => void
}

const COLUMNS = 12
type Show = 'all' | 'breaks' | 'owed' | 'changed'

const ROW = '[data-testid="record-row"]'

export default function FundRecordTab({
  fundId,
  table,
  fullTable = table,
  tasks,
  masters = [],
  matrix,
  ledger,
  lens,
  contextual,
  allowEdits = !lens,
  editScope,
  onRecordChanged,
  onOpen,
  onEvidence,
}: Props) {
  const evidencedLens = lens?.mode === 'evidenced'
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<'hierarchy' | 'alphabetical'>('hierarchy')
  const [exportError, setExportError] = useState<unknown>(null)
  const [exporting, setExporting] = useState(false)
  const [show, setShow] = useState<Show>('all')
  const [selected, setSelected] = useState<string | null>(() =>
    new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get(
      'record_edit_position'
    )
  )
  const tableRef = useRef<HTMLTableElement | null>(null)

  /* J / K move the focus ring line by line — the line IS the cursor
   * (keyboard canon); Enter on a line opens its ledger box, Esc closes the
   * box and hands focus back to the line. With a dialog open (the drawer,
   * a help popover) Esc belongs to the dialog. */
  const onNext = useCallback(() => focusRow(tableRef.current, ROW, 1), [])
  const onPrev = useCallback(() => focusRow(tableRef.current, ROW, -1), [])
  const closeBox = useCallback(() => {
    if (!selected) return
    if (document.activeElement?.closest('[role="dialog"]')) return
    setSelected(null)
    const row = document.getElementById(`rx-${selected}`)
    if (row instanceof HTMLElement) row.focus()
  }, [selected])
  useListKeys({ onNext, onPrev, onEscape: selected ? closeBox : undefined })

  /* when the ledger was read: the payload's arrival (the heartbeat re-read
   * moves it) */
  const [readAt, setReadAt] = useState(() => new Date())
  useEffect(() => setReadAt(new Date()), [table])

  const trades = table.trades as LedgerTrade[]
  const financialTrades = fullTable.trades as LedgerTrade[]
  const financialSegments = useMemo(
    () => new Map(fullTable.segments.map((segment) => [segment.position_id, segment])),
    [fullTable.segments]
  )
  const instrumentById = useMemo(
    () => new Map(fullTable.instruments.map((i) => [i.id, i])),
    [fullTable.instruments]
  )
  const root = useMemo(
    () => fundRootId(fullTable.segments.filter((s) => s.group === 'chain')),
    [fullTable.segments]
  )

  const masterByEntity = useMemo(() => new Map(masters.map((m) => [m.entity_id, m])), [masters])

  /* The break chip reads the open tasks plus the ledger's standing breaks
   * (a snoozed card is not in the open queue but IS a break — batch B). */
  const recoTasks = useMemo(() => {
    const seen = new Set(tasks.map((t) => t.task_id))
    const extra = (ledger?.breaks?.items ?? [])
      .filter((i) => !seen.has(i.task_id) && RECON_TASK_TYPES.has(i.task_type))
      .map(breakItemAsTask)
    return extra.length ? [...tasks, ...extra] : tasks
  }, [tasks, ledger])

  /* Every position on the record, one line each: the chain's legs and the
   * inbound positions in the fund's own instruments. Proposed never
   * renders in the fund area. */
  const lines = useMemo(() => {
    const segs = table.segments.filter((s) => s.state !== 'proposed')
    return segs
      .map((visibleSegment) => {
        const s = financialSegments.get(visibleSegment.position_id) ?? visibleSegment
        const cb = isCommitmentBased(s)
        const balance = balanceOf(s, financialTrades)
        const commitment = cb ? statedCommitment(s) : null
        const task = breakTaskOf(s, recoTasks)
        const delta = task ? taskDelta(task) : null
        const doc = balanceDemand(s, matrix)
        return {
          seg: s,
          cb,
          figure: cb ? null : balance.figure,
          asOf: balance.asOf,
          commitment,
          ev: evOf(s, financialTrades, matrix),
          task,
          delta,
          doc,
          movements: positionMovements(s, trades).length,
        }
      })
      .sort((a, b) => (sort === 'alphabetical' ? flatOrder(a.seg, b.seg) : 0))
  }, [table.segments, financialSegments, trades, financialTrades, recoTasks, matrix, sort])

  const counts: RecordCounts = useMemo(() => {
    const since = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)
    return ledger
      ? countsFromLedger(ledger)
      : recordCounts({
          segments: fullTable.segments,
          trades: financialTrades,
          tasks,
          matrix,
          since,
        })
  }, [ledger, fullTable.segments, financialTrades, tasks, matrix])

  const changedSince = counts.since
  const visible = lines.filter((l) => {
    if (show === 'breaks' && !l.task) return false
    if (show === 'owed' && l.ev.proven >= l.ev.total) return false
    if (show === 'changed' && !positionChangedSince(l.seg, financialTrades, changedSince))
      return false
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return [l.seg.holder_name, l.seg.instrument_name, l.seg.issuer_name, l.doc?.document_name]
      .filter((v): v is string => !!v)
      .some((v) => v.toLowerCase().includes(q))
  })

  /* the box follows the record: a selected line that leaves the view
   * (a lens, a find) closes it */
  useEffect(() => {
    if (selected && !visible.some((l) => l.seg.position_id === selected)) setSelected(null)
  }, [selected, visible])

  const exportXlsx = async () => {
    if (contextual) {
      contextual.onExport(visible.map((line) => line.seg))
      return
    }
    if (exporting || !fundId) return
    setExporting(true)
    setExportError(null)
    try {
      const blob = await apiFetchBlob(
        `/api/funds/${fundId}/record-table/export?sheet=segments&state=recorded&include_proposed=false`
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${table.fund_name}-record.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setExportError(error)
    } finally {
      setExporting(false)
    }
  }

  const select = (id: string) => setSelected((cur) => (cur === id ? null : id))
  const chip = (k: Show, n: number, label: string, cls: string, testId: string) => (
    <button
      type="button"
      className={`fa-cchip ${cls}${n === 0 ? ' zero' : ''}${show === k ? ' on' : ''}`}
      onClick={() => setShow((cur) => (cur === k ? 'all' : k))}
      aria-pressed={show === k}
      data-testid={testId}
      data-count={n}
    >
      <b>{n}</b>
      {label}
    </button>
  )

  const docCell = (l: (typeof lines)[number]) => {
    const d = l.doc
    if (d && (d.document_id || d.document_name)) {
      const name = 'Source reference unavailable'
      if (!d.document_id)
        return (
          <span className="fa-docchip still" data-testid="record-doc">
            {name}
          </span>
        )
      return (
        <DocumentLink
          className="fa-docchip"
          document={{ document_id: d.document_id, page: d.page }}
          context={{ fundId, tab: 'record', focus: `segment:${l.seg.position_id}` }}
          onClick={(e) => e.stopPropagation()}
          data-testid="record-doc"
          title="Open the document"
        />
      )
    }
    if (d && !isProven(d.status) && d.is_mandatory)
      return (
        <span className="miss" data-testid="record-doc-owed">
          ✗ owed
        </span>
      )
    return <span className="fa-none">—</span>
  }

  const selectedLine = selected ? lines.find((l) => l.seg.position_id === selected) : null

  return (
    <div data-testid="fund-record">
      <div className="fa-card">
        <div className="fa-toolbar fa-flatbar" data-testid="record-bar">
          {lens ? (
            <span className="fa-lens" data-testid="rebuild-lens">
              {lens.label}
            </span>
          ) : null}
          <input
            className="fa-search fa-find"
            aria-label="Find in the record"
            placeholder="find — holder, instrument, company, document"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="record-filter"
          />
          {chip(
            'breaks',
            counts.breaks,
            counts.breaks === 1 ? 'break' : 'breaks',
            'brk',
            'record-chip-breaks'
          )}
          {chip('owed', counts.owed, 'owed', 'owe', 'record-chip-owed')}
          {chip(
            'changed',
            counts.changed,
            `changed since ${formatOfficerDate(changedSince)}`,
            'dim',
            'record-chip-changed'
          )}
          <label className="nav-sort">
            Sort{' '}
            <select
              aria-label="Sort record"
              value={sort}
              onChange={(event) => setSort(event.target.value as 'hierarchy' | 'alphabetical')}
            >
              <option value="hierarchy">Hierarchy</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </label>
          <span className="fa-export">
            <button type="button" className="fa-tlink" disabled={exporting} onClick={exportXlsx}>
              {exporting
                ? 'Downloading…'
                : contextual
                  ? 'Export this view .csv'
                  : 'Export full record .xlsx'}
            </button>{' '}
            <HelpDot k="record.table" />
            {!contextual && (lens || show !== 'all' || query) && (
              <small>Exports today’s full record</small>
            )}
          </span>
        </div>
        {exportError ? (
          <ErrorState
            what="record export"
            error={exportError}
            onRetry={() => void exportXlsx()}
            testId="record-export-error"
          />
        ) : null}
        <div className="fa-flatwrap">
          <table className="fa-rt fa-flat" data-testid="record-table" ref={tableRef}>
            <colgroup>
              <col style={{ width: '15.6%' }} />
              <col style={{ width: '16.4%' }} />
              <col style={{ width: '15.4%' }} />
              <col style={{ width: '6.8%' }} />
              <col style={{ width: '6.8%' }} />
              <col style={{ width: '2.9%' }} />
              <col style={{ width: '3.3%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '6.5%' }} />
              <col style={{ width: '4.3%' }} />
              <col style={{ width: '3.8%' }} />
              {/* RECO holds the break chip whole — "break 9,000 ›" never
                  truncates, or the officer cannot read the figure. */}
              <col style={{ width: '10.7%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="g-id">holder</th>
                <th className="g-id">instrument</th>
                <th className="g-id">issuer</th>
                <th className="num">balance</th>
                <th className="num">issued</th>
                <th>ccy</th>
                <th className="num">%</th>
                <th>as of</th>
                <th className="g-pf">document</th>
                <th className="num g-pf">trades</th>
                <th className="num g-pf">ev</th>
                <th className="num g-pf">break</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => {
                const s = l.seg
                const key = s.position_id
                const isSel = selected === key
                const instrument = s.instrument_id ? instrumentById.get(s.instrument_id) : null
                const ccy = s.units_currency ?? instrument?.currency ?? null
                const breakHref =
                  contextual?.breakHref(s) ??
                  (fundId
                    ? `/v2/workbench/reconciliation?fund=${encodeURIComponent(fundId)}&task=${l.task?.task_id ?? ''}`
                    : null)
                const issued = instrument?.outstanding_units ?? null
                const cls = [isSel ? 'sel' : '', l.cb ? 'hassub' : ''].filter(Boolean).join(' ')
                const cell = (
                  label: string | null,
                  target: DrawerTarget | null,
                  testId: string,
                  colour: string
                ) =>
                  target && label ? (
                    <button
                      type="button"
                      className={`fa-cell ${colour}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onOpen(target)
                      }}
                      data-testid={testId}
                      title={label}
                    >
                      {label}
                    </button>
                  ) : (
                    <span className={`fa-cell ${colour}`} data-testid={testId}>
                      {label ?? '—'}
                    </span>
                  )
                const main = (
                  <tr
                    key={`${key}-main`}
                    id={`rx-${key}`}
                    className={cls || undefined}
                    data-testid="record-row"
                    data-position={key}
                    data-selected={isSel || undefined}
                    data-package={l.cb || undefined}
                    tabIndex={0}
                    onClick={() => select(key)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        select(key)
                      }
                    }}
                    title="opens the trades of this position"
                  >
                    <td className="hold">
                      {cell(
                        s.holder_name,
                        { type: 'entity', id: s.holder_id },
                        'record-holder',
                        'ent'
                      )}
                    </td>
                    <td className="pos">
                      {contextual ? (
                        <button
                          className="nav-position-door"
                          type="button"
                          aria-label={`Open position: ${s.holder_name} · ${s.instrument_name} · ${s.issuer_name}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            contextual.onPosition(s)
                          }}
                          data-testid="record-position-door"
                        >
                          ›
                        </button>
                      ) : (
                        <span className="tdot pos" aria-hidden="true" />
                      )}
                      {cell(
                        s.instrument_name,
                        contextual && s.instrument_id
                          ? { type: 'instrument', id: s.instrument_id }
                          : { type: 'position', id: s.position_id },
                        'record-instrument',
                        'pos'
                      )}
                    </td>
                    <td className="iss">
                      {cell(
                        s.issuer_name,
                        s.issuer_id ? { type: 'entity', id: s.issuer_id } : null,
                        'record-issuer',
                        'ent'
                      )}
                    </td>
                    <td className="num fig" data-testid="record-balance-cell">
                      {figure(l.figure)}
                    </td>
                    <td
                      className={`num ${issued !== null ? 'fig' : 'ns'}`}
                      data-testid="record-issued"
                    >
                      {issued !== null ? figure(issued) : '—'}
                    </td>
                    <td className={`mono ${ccy ? '' : 'ns'}`} data-testid="record-ccy">
                      {ccy ?? '—'}
                    </td>
                    <td
                      className={`num ${!l.cb && s.economic_pct !== null ? 'fig' : 'ns'}`}
                      data-testid="record-economic-pct"
                    >
                      {!l.cb && s.economic_pct !== null ? `${Math.round(s.economic_pct)}%` : '—'}
                    </td>
                    <td className={`mono ${l.asOf ? '' : 'ns'}`} data-testid="record-asof">
                      {tableDate(l.asOf) ?? '—'}
                    </td>
                    <td className="doc" data-testid="record-document">
                      {docCell(l)}
                    </td>
                    <td className="num">
                      <button
                        type="button"
                        className={`fa-tdoor${isSel ? ' open' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          select(key)
                        }}
                        aria-expanded={isSel}
                        data-testid="record-trades-door"
                        title={
                          isSel ? 'closes the trades box' : 'opens the trades of this position'
                        }
                      >
                        {l.movements} {isSel ? '▾' : '›'}
                      </button>
                    </td>
                    <td
                      className={`num fa-evfrac ${evidencedLens || l.ev.total === 0 ? 'ns' : l.ev.proven >= l.ev.total ? 'full' : 'part'}`}
                      data-testid="record-ev"
                    >
                      {evidencedLens || l.ev.total === 0 ? (
                        '—'
                      ) : (
                        <button
                          type="button"
                          className="fa-docchip"
                          data-testid="record-ev-door"
                          title="Open evidence for this position"
                          onClick={(event) => {
                            event.stopPropagation()
                            onEvidence({ type: 'position', id: s.position_id })
                          }}
                        >
                          {`${l.ev.proven}/${l.ev.total}`}
                        </button>
                      )}
                    </td>
                    <td
                      className="num"
                      data-testid="record-reco"
                      data-delta={l.task ? String(l.delta ?? 0) : '0'}
                    >
                      {l.task && breakHref ? (
                        <Link
                          className="fa-brkchip"
                          to={breakHref}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="record-reco-door"
                          title={
                            contextual ? 'Open the subchain break' : 'opens the reconciliation task'
                          }
                        >
                          {l.delta !== null ? breakWords(l.delta) : 'break'} ›
                        </Link>
                      ) : (
                        <span className="fa-none">—</span>
                      )}
                    </td>
                  </tr>
                )
                /* 16d: a fund-units line (the issuer is a master) carries a
                   "through …" sub-line — the master's OWN record as a door.
                   The master's demands are counted once, never folded into
                   this line's EV or break column; a click LEAVES the feeder. */
                const master = l.seg.issuer_id ? masterByEntity.get(l.seg.issuer_id) : null
                const through = master
                  ? (() => {
                      const t = throughLine(master)
                      return (
                        <tr
                          key={`${key}-through`}
                          className={`sub last${isSel ? ' selb' : ''}`}
                          data-testid="record-through"
                          onClick={() => select(key)}
                        >
                          <td />
                          <td className="lab" colSpan={COLUMNS - 1}>
                            the master&rsquo;s own record — <b>{t.proven}</b> of <b>{t.total}</b>{' '}
                            demands proven
                            {t.breaks > 0 ? (
                              <>
                                , <b>{t.breaks}</b> break{t.breaks === 1 ? '' : 's'} open
                              </>
                            ) : null}
                            , <b>{t.targets}</b> target asset{t.targets === 1 ? '' : 's'} ·{' '}
                            <Link
                              className="fa-tlink"
                              to={`/v2/funds/${t.fundId}?tab=record`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid="record-through-door"
                              data-fund={t.fundId}
                            >
                              open {t.name} ›
                            </Link>
                          </td>
                        </tr>
                      )
                    })()
                  : null
                if (!l.cb || !l.commitment) return through ? [main, through] : main
                const sub = (
                  line: 'commitment' | 'drawn' | 'undrawn',
                  v: number | null,
                  last: boolean
                ) => (
                  <tr
                    key={`${key}-${line}`}
                    className={`sub${last ? ' last' : ''}${isSel ? (last ? ' selb' : ' selm') : ''}`}
                    data-testid="record-row-line"
                    data-line={line}
                    onClick={() => select(key)}
                  >
                    <td />
                    <td className="lab">{line}</td>
                    <td />
                    <td className="num fig">{figure(v)}</td>
                    <td colSpan={COLUMNS - 4} />
                  </tr>
                )
                return [
                  main,
                  sub('commitment', l.commitment.commitment, false),
                  sub('drawn', l.commitment.drawn, false),
                  sub('undrawn', l.commitment.undrawn, !through),
                  ...(through ? [through] : []),
                ]
              })}
              {visible.length === 0 ? (
                <tr data-testid="record-empty">
                  <td colSpan={COLUMNS} className="ns">
                    {lines.length === 0 ? 'no positions on the record' : 'nothing matches'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="fa-tfoot" data-testid="record-foot">
          <span>
            {positionsFootWords(
              visible.map((l) => l.seg),
              root
            )}
          </span>
          <span className="rd">
            ledger read{' '}
            <b>
              {formatOfficerDate(readAt.toISOString())} ·{' '}
              {readAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </b>
          </span>
        </div>
      </div>
      {selectedLine ? (
        <>
          {allowEdits ? (
            <RecordEditTargets
              segment={selectedLine.seg}
              table={table}
              matrix={matrix}
              scope={
                editScope
                  ? editScope(selectedLine.seg)
                  : fundId
                    ? {
                        fund_id: fundId,
                        ...(selectedLine.seg.instrument_id && selectedLine.seg.issuer_id
                          ? {
                              holder_id: selectedLine.seg.holder_id,
                              instrument_id: selectedLine.seg.instrument_id,
                              issuer_id: selectedLine.seg.issuer_id,
                            }
                          : {}),
                      }
                    : null
              }
              onRecordChanged={onRecordChanged}
            />
          ) : null}
          <PositionLedger
            fundId={fundId}
            seg={selectedLine.seg as RtSegment}
            table={table}
            fullTable={fullTable}
            matrix={matrix}
            tasks={tasks}
            onClose={closeBox}
            onOpen={onOpen}
            onEvidence={onEvidence}
            breakHref={contextual?.breakHref(selectedLine.seg)}
            addMovementHref={(() => {
              const actualFund = editScope ? editScope(selectedLine.seg)?.fund_id : fundId
              return allowEdits && actualFund && selectedLine.seg.state !== 'proposed'
                ? manualMovementHref(actualFund, {
                    kind: 'position',
                    id: selectedLine.seg.position_id,
                  })
                : undefined
            })()}
          />
        </>
      ) : null}
    </div>
  )
}
